import { Alert, SkeletonRows, Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DigestPreviewPanel,
  NotificationSettingsForm,
} from "../../../features/alarms/notification-settings.js";
import { PreferenceMatrix } from "../../../features/alarms/preference-matrix.js";
import {
  useAlarmPermissions,
  useNotificationPreferences,
  useNotificationSettings,
} from "../../../features/alarms/queries.js";
import { NotificationsPage } from "../../../features/alarms/shared.js";
import { TestNotificationButton } from "../../../features/alarms/test-notification.js";
import { errorMessage } from "../../../lib/errors.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const TABS = ["matrix", "settings", "preview"] as const;
const searchSchema = z.object({ tab: z.enum(TABS).optional().catch(undefined) });

export const Route = createFileRoute("/_app/bildirimler/tercihler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NotificationPreferencesRoute,
});

/**
 * Bildirim tercihleri (Prompt 20 web): tercih matrisi (tümü / kategori / kural × kanal; istasyon seçici), sessiz saat ve
 * özet ayarları, sabah / haftalık özet önizlemesi ve test bildirimi. Kişiseldir (izin istemez; `ALERTS` bayrağı).
 */
function NotificationPreferencesRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab = search.tab ?? "matrix";
  const permissions = useAlarmPermissions();
  const shape = useTenantShape();
  const preferences = useNotificationPreferences(permissions.enabled && tab === "matrix");
  const settings = useNotificationSettings(permissions.enabled && tab === "settings");
  return (
    <NotificationsPage
      title={t("menu.notificationPreferences")}
      description={t("notifications.preferences.description")}
      breadcrumb={[
        {
          label: t("menu.notifications"),
          link: (children, className) => (
            <Link to="/bildirimler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: t("menu.notificationPreferences") },
      ]}
      actions={<TestNotificationButton />}
    >
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: ".",
            search: (value === "matrix" ? {} : { tab: value }) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("notifications.preferences.tabs")} className="overflow-x-auto">
          <TabsTrigger value="matrix">{t("notifications.preferences.matrixTab")}</TabsTrigger>
          <TabsTrigger value="settings">{t("notifications.preferences.settingsTab")}</TabsTrigger>
          <TabsTrigger value="preview">{t("notifications.preferences.previewTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="matrix">
          {preferences.error ? (
            <Alert tone="danger">{errorMessage(preferences.error)}</Alert>
          ) : preferences.isPending ? (
            <SkeletonRows rows={8} />
          ) : (
            <PreferenceMatrix preferences={preferences.data} showStationPicker={!shape.isSingleStation} />
          )}
        </TabsContent>
        <TabsContent value="settings">
          {settings.error ? (
            <Alert tone="danger">{errorMessage(settings.error)}</Alert>
          ) : settings.isPending ? (
            <SkeletonRows rows={6} />
          ) : (
            <NotificationSettingsForm settings={settings.data} />
          )}
        </TabsContent>
        <TabsContent value="preview">
          <DigestPreviewPanel />
        </TabsContent>
      </Tabs>
    </NotificationsPage>
  );
}
